import { DoneActorEvent } from 'xstate';
import z from 'zod';
import { ERROR_MESSAGES } from '~/constants/errorMessages';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import type { FeeRefusalCode } from '~/features/market/listing-search/errors';
import { optionSchemaRequired } from '~/features/types';
import { nonNegativeInt, positiveNumber } from '~/features/utils';
import { EmployerUser, Provider } from '~/types';
import { JobNegotiation } from '~/types/hlx/jobNegotiation';
import { JobResponse } from '~/types/hlx/jobResponse';
import { MsaasNegotiation } from '~/types/hlx/msaasNegotiation';
import type { BeginNegotiationResponse } from './beginNegotiation';
import { getShiftAllocationError } from './utils';

export type NegotiationResponseType = 'msaas' | 'job';
export type NegotiationParty = 'EMPLOYER' | 'PROVIDER';

// [MCV-1646] A draft is identified either by an existing JobResponse (a chain
// already exists but is still empty -- e.g. a first APPLIED response) or by a
// bare listing (nothing exists yet). Never a fake id/version -- the server
// mints those on submit.
export type NegotiationDraftSource =
  | { responseId: number }
  | { listingType: 'JOB' | 'MSAAS'; listingId: number };

export type Context = {
  employerUser: EmployerUser | undefined;
  provider: Provider | null;
  viewerRole: NegotiationParty | undefined;
  responseId: number | undefined;
  responseType: NegotiationResponseType;
  negotiations: NegotiationFormValues[] | undefined;
  negotiation: NegotiationFormValues | undefined;
  tabIndex: number;
  loading: boolean;
  /**
   * [MCV-1557] The by-response load failed, as opposed to succeeding with an
   * empty chain. Both leave `negotiation` undefined; only the latter means
   * "nothing here yet".
   */
  loadFailed: boolean;
  isReadOnly: boolean;
  offerModal: {
    open: boolean;
  };
  latestVersion: number;
  // [MCV-1646] draft-first initial offer.
  draft: NegotiationDraft | undefined;
  draftSource: NegotiationDraftSource | undefined;
  draftProviderIds: number[] | undefined;
  startDraftIfEmpty: boolean;
  submitting: boolean;
  /**
   * Remains true until the submit POST and any authoritative reconciliation
   * load have both settled. Unlike `submitting`, this intentionally spans the
   * transition through $_LOAD so the singleton actor cannot be retargeted and
   * its modal cannot be dismissed while the outcome is still indeterminate.
   */
  submitLifecycleBusy: boolean;
  /** Shared deadline for all reads that reconcile one submission. */
  recoveryDeadlineAt?: number;
  submittedDraft?: { data: NegotiationFormValues; providerIds?: number[] };
  submitResult: DraftSubmitResult | undefined;
  /** Scope that produced submitResult. The negotiation actor is a singleton. */
  submitResultSource: NegotiationDraftSource | undefined;
  submitError: { status?: number; message: string; uncertain?: boolean } | undefined;
  /**
   * [MCV-1816] Set when a counter offer POST succeeds. The details view consumes
   * it (toast + close the popup) and then clears it, so a result left behind by
   * an unmounted view never closes the next negotiation that mounts. `at` is the
   * identity the consumer names when it clears the result.
   * [MCV-1850] A decline or withdrawal closes the popup the same way, and says
   * which it was in `kind`.
   */
  counterResult: CounterResult | undefined;
  /**
   * [MCV-1858] Set when an accept is refused for its platform-fee schedule (428 not authorized,
   * 409 changed since the preview). The details view consumes it by refetching the schedule and
   * reopening the fee dialog.
   */
  acceptRefusal: AcceptRefusal | undefined;
};

export type AcceptRefusal = {
  responseId: number | undefined;
  at: number;
  code: FeeRefusalCode;
};

export type CounterResult = {
  responseId: number | undefined;
  at: number;
  /** Absent means a counter offer. */
  kind?: 'decline' | 'withdraw';
};

export type OnLoadEvent = {
  type: 'ON_LOAD';
  payload: {
    employerUser: EmployerUser | undefined;
    responseId: number;
    responseType: NegotiationResponseType;
    viewerRole?: NegotiationParty;
    startDraftIfEmpty?: boolean;
  };
};

export type OnOpenDraftEvent = {
  type: 'ON_OPEN_DRAFT';
  payload: {
    employerUser: EmployerUser | undefined;
    viewerRole?: NegotiationParty;
    listingType: 'JOB' | 'MSAAS';
    listingId: number;
    providerIds?: number[];
  };
};

export type OnSubmitDraftEvent = {
  type: 'ON_SUBMIT_DRAFT';
  data: NegotiationFormValues;
  providerIds?: number[];
};

export type OnConsumeSubmitResultEvent = {
  type: 'ON_CONSUME_SUBMIT_RESULT';
  payload: { negotiationId: number };
};

export type OnConsumeCounterResultEvent = {
  type: 'ON_CONSUME_COUNTER_RESULT';
  payload: { at: number };
};

export type OnCalculateEvent = {
  type: 'ON_CALCULATE';
  payload: { values: NegotiationFormValues };
};

export type OnSetTabIndexEvent = {
  type: 'ON_SET_TAB_INDEX';
  payload: { index: number };
};

export type OnAcceptOfferEvent = {
  type: 'ON_ACCEPT_OFFER';
  data: NegotiationFormValues;
  /** [MCV-1858] The B2B payer's acknowledged platform-fee schedule. */
  feeAuthorization?: FeeAuthorization;
};
export type OnConsumeAcceptRefusalEvent = {
  type: 'ON_CONSUME_ACCEPT_REFUSAL';
  payload: { at: number };
};
export type OnCloseOfferModalEvent = {
  type: 'ON_CLOSE_OFFER'; // need a better name, this is "close offer modal"
};
export type OnReviewOfferModalEvent = {
  type: 'ON_REVIEW_OFFER';
  id: number;
};
export type OnRejectOfferEvent = {
  type: 'ON_REJECT_OFFER';
  payload: { id: string };
};
export type OnWithdrawOfferEvent = {
  type: 'ON_WITHDRAW_OFFER';
  payload: { id: string };
};
export type OnCounterOfferEvent = {
  type: 'ON_COUNTER_OFFER';
  data: NegotiationFormValues;
};

export type OfferEvents =
  | OnAcceptOfferEvent
  | OnCloseOfferModalEvent
  | OnRejectOfferEvent
  | OnWithdrawOfferEvent
  | OnReviewOfferModalEvent
  | OnCounterOfferEvent;

export type DoneGetEvent = DoneActorEvent<NegotiationFormValues[], string>;

export type OnSetEmployerUser = {
  type: 'ON_SET_EMPLOYER_USER';
  payload: { employerUser: EmployerUser };
};

export type OnSetProvider = {
  type: 'ON_SET_PROVIDER';
  payload: { provider: Provider };
};

export type Events =
  | { type: 'ON_RETRY_SUBMIT_DRAFT' }
  | OnLoadEvent
  | OnOpenDraftEvent
  | OnSubmitDraftEvent
  | OnConsumeSubmitResultEvent
  | OnConsumeCounterResultEvent
  | OnConsumeAcceptRefusalEvent
  | DoneGetEvent
  | OnSetTabIndexEvent
  | OnCalculateEvent
  | OfferEvents
  | OnSetEmployerUser
  | OnSetProvider;

export type Scope = {
  context: Context;
  event: Events;
};

const negotiationSchemaShape = {
  //Contract Overview
  startDate: z.iso.date(),
  endDate: z.string().optional().nullable(),
  // MSaaS-only fields are optional in the shared shape so JOB drafts do not
  // fail validation for omitting columns their API never returns. MSaaS adds
  // its required-field checks in buildNegotiationSchema below. numOfFtes must be
  // a whole number (the server rejects a fractional num_of_ftes in every labor
  // mode); MSaaS requires it to also be positive via requirePositive.
  numOfFtes: nonNegativeInt.optional(),
  fteShiftsPerYear: nonNegativeInt.optional(),
  // JOB-only. MSaaS derives this from numOfFtes * fteShiftsPerYear.
  numOfShifts: nonNegativeInt.optional(),
  hoursPerShift: positiveNumber,
  // Shift Allocation
  numDayShifts: nonNegativeInt,
  numEveShifts: nonNegativeInt,
  numNightShifts: nonNegativeInt,
  numOfWeDayShifts: nonNegativeInt,
  numOfWeEveShifts: nonNegativeInt,
  numOfWeNightShifts: nonNegativeInt,
  numHolDayShifts: nonNegativeInt,
  numHolEveShifts: nonNegativeInt,
  numHolNightShifts: nonNegativeInt,
  // Compensation
  // Calculated by the server after persistence. A draft carries the listing's
  // persisted total (top-level on the negotiation-draft response) for display.
  totalContractValue: positiveNumber.optional(),
  compType: optionSchemaRequired,
  pricePerHour: positiveNumber,
  pricePerShift: positiveNumber,
  pricePerRvu: positiveNumber,
  yearlyRvusPerFte: nonNegativeInt.optional(),
  // Calculated by the server after persistence; carried by drafts like the total.
  avgHourlyRate: positiveNumber.optional(),
  // Shift Differentials
  shiftDiffType: optionSchemaRequired,
  eveShiftRateDiff: positiveNumber,
  nightShiftRateDiff: positiveNumber,
  weDayShiftRateDiff: positiveNumber,
  weEveShiftRateDiff: positiveNumber,
  weNightShiftRateDiff: positiveNumber,
  holDayShiftRateDiff: positiveNumber,
  holEveShiftRateDiff: positiveNumber,
  holNightShiftRateDiff: positiveNumber,
  // Contract Terms
  bonusesTxt: z.string(),
  adminCostsTxt: z.string(),
  extraTxt: z.string(),
  infrastructureTxt: z.string(),
};

/**
 * [MCV-1645] MSaaS negotiations additionally enforce that the nine shift
 * buckets add up to the expected total (numOfFtes * fteShiftsPerYear); JOB
 * negotiations do not carry the shift-allocation UI and skip the rule.
 */
export const buildNegotiationSchema = (responseType: NegotiationResponseType) =>
  z.object(negotiationSchemaShape).superRefine((data, ctx) => {
    if (!(!data.endDate || data.startDate < data.endDate)) {
      ctx.addIssue({
        code: 'custom',
        message: ERROR_MESSAGES.INVALID_START_END_DATES,
        path: ['startDate'],
      });
    }
    const requirePositive = (
      field:
        | 'numOfFtes'
        | 'fteShiftsPerYear'
        | 'numOfShifts'
        | 'hoursPerShift'
        | 'pricePerHour'
        | 'pricePerShift'
        | 'pricePerRvu'
        | 'yearlyRvusPerFte'
    ) => {
      if (data[field] == null) {
        ctx.addIssue({
          code: 'custom',
          message: ERROR_MESSAGES.REQUIRED,
          path: [field],
        });
      } else if (Number(data[field]) <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: ERROR_MESSAGES.POSITIVE_INT,
          path: [field],
        });
      }
    };

    if (responseType === 'msaas') {
      for (const field of ['numOfFtes', 'fteShiftsPerYear', 'hoursPerShift'] as const) {
        requirePositive(field);
      }

      const allocationError = getShiftAllocationError(data);
      if (allocationError) {
        ctx.addIssue({
          code: 'custom',
          message: allocationError,
          path: ['root', 'shiftAllocation'],
        });
      }

      if (data.compType.value === 'RVU') {
        requirePositive('yearlyRvusPerFte');
      }
    } else {
      // JOB drafts and counters must not submit values the backend rejects.
      // Historical nullable JOB offers remain renderable because validation
      // runs only for draft/counter actions; JOB Accept bypasses it below.
      for (const field of ['numOfShifts', 'hoursPerShift'] as const) {
        requirePositive(field);
      }
    }

    const compensationAnchor = {
      HOUR: 'pricePerHour',
      SHIFT: 'pricePerShift',
      RVU: 'pricePerRvu',
    }[data.compType.value] as 'pricePerHour' | 'pricePerShift' | 'pricePerRvu' | undefined;
    if (compensationAnchor) requirePositive(compensationAnchor);
  });

export const negotiationSchema = buildNegotiationSchema('msaas');

export type NegotiationRecord = MsaasNegotiation | JobNegotiation;
export type NegotiationSchema = z.infer<typeof negotiationSchema>;

type NegotiationRecordFields<T> = T extends NegotiationRecord
  ? Omit<T, 'compType' | 'shiftDiffType'>
  : never;
type NegotiationOption = z.infer<typeof optionSchemaRequired>;

export type NegotiationFormValues = NegotiationRecordFields<NegotiationRecord> & {
  numOfFtes?: number | null;
  fteShiftsPerYear?: number | null;
  yearlyRvusPerFte?: number | null;
  compType: NegotiationOption;
  shiftDiffType: NegotiationOption;
  responseType: NegotiationResponseType;
  bonusesTxtFormatted?: string | null;
  adminCostsTxtFormatted?: string | null;
  extraTxtFormatted?: string | null;
  infrastructureTxtFormatted?: string | null;
};

// [MCV-1646] Draft terms are exactly the editable NegotiationFormValues minus
// the fields the server mints (id/version/sender/senderParty) or attaches
// (jobResponseId/jobResponse/previousNegotiationId) -- a draft has none of
// those yet. `Partial` loosens the id-shaped record fields that
// `NegotiationRecordFields` otherwise requires (e.g. relationship objects),
// since a draft is built from a listing, not a persisted negotiation row.
export type NegotiationDraftTerms = Partial<
  Omit<
    NegotiationFormValues,
    | 'id'
    | 'version'
    | 'sender'
    | 'senderParty'
    | 'jobResponseId'
    | 'jobResponse'
    | 'previousNegotiationId'
  >
> &
  Pick<NegotiationFormValues, 'compType' | 'shiftDiffType' | 'responseType'>;

export type NegotiationDraft = {
  kind: 'draft';
  responseType: NegotiationResponseType;
  source: NegotiationDraftSource;
  providerIds?: number[];
  listingTitle?: string;
  listingOwnerName?: string | null;
  responderName?: string | null;
  senderRole?: string;
  terms: NegotiationDraftTerms;
};

export type NegotiationViewMode =
  | 'loading'
  | 'error'
  | 'empty'
  | 'draft'
  | 'submitting'
  | 'actionable'
  | 'waiting'
  | 'history';

export type DraftSubmitResult = {
  jobResponseId: number;
  negotiationId: number;
  responseType: BeginNegotiationResponse['responseType'];
  createdNegotiation: boolean;
  jobResponse: JobResponse;
};
